import classNames from "classnames";
import React, { useContext, useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import TemplateContext from "../../../contexts/TemplateContext";
import { useScrollLock } from "../../../hooks/useScrollLock.hook";
import { ButtonProps } from "../../../interfaces/Button.interface";
import { NavItem } from "../../../interfaces/Navigation.interface";
import { StoryblokAsset } from "../../../interfaces/Storyblok/StoryblokAsset.interface";
import { StoryblokLink } from "../../../interfaces/Storyblok/StoryblokLink.interface";
import Headroom from "react-headroom";
import "../../../styles/headroom.css";
import { StoryblokNode } from "../../../interfaces/Storyblok/StoryblokNode.interface";
import MainNav from "../../molecules/MainNav/MainNav.molecule";
import PreNav from "../../molecules/PreNav/PreNav.molecule";
import SideNav from "../../molecules/SideNav/SideNav.molecule";
import { graphql, useStaticQuery } from "gatsby";
import Banner from "../Banner/Banner.blok";
import getBannerBlok from "../../../utils/getBannerBlok.util";
import { isBrowser } from "../../../utils/isBrowser.util";

interface HeaderProps {
  pre_navigation_items: NavItem[];
  login_link: StoryblokLink;
  social_links: StoryblokNode[];
  logo: StoryblokAsset;
  main_navigation_items: NavItem[];
  primary_cta: ButtonProps[];
  is_search_enabled: boolean;
  search_link: StoryblokLink;
  search_bar_placeholder: string;
  search_bar_button_label: string;
  is_find_us_search_enabled: boolean;
  find_us_link: StoryblokLink;
  find_us_placeholder: string;
  find_us_button_label: string;
}

const Header: React.FC<HeaderProps> = ({
  pre_navigation_items,
  social_links,
  logo,
  main_navigation_items,
  primary_cta,
  is_search_enabled,
  search_link,
  search_bar_placeholder,
  search_bar_button_label,
  login_link,
  is_find_us_search_enabled,
  find_us_link,
  find_us_placeholder,
  find_us_button_label,
  ...props
}) => {
  const { banners } = useStaticQuery(graphql`
    {
      banners: allStoryblokEntry(sort: { fields: id }, filter: { field_component: { eq: "Banner" } }) {
        edges {
          node {
            name
            full_slug
            content
          }
        }
      }
    }
  `);
  const [sideNav, setSideNav] = useState<boolean>(false);
  const [navIsFixed, setNavIsFixed] = useState<boolean>(false);
  const [navIsUnpinned, setNavIsUnpinned] = useState<boolean>(false);
  const toggleSideNav = (state: boolean): void => setSideNav(state);
  const { lockScroll, unlockScroll } = useScrollLock();
  const [showBanner, setShowBanner] = useState(false);
  const { contentType, hideTopBanner } = useContext(TemplateContext);
  const bannerBlok = getBannerBlok(banners, contentType ?? "Page");

  const preNavRef = React.useRef<HTMLDivElement>(null);
  const mainNavRef = React.useRef<HTMLDivElement>(null);
  const [preNavHeight, setPreNavHeight] = useState<number>(0);
  const [mainNavHeight, setMainNavHeight] = useState<number>(0);

  useEffect(() => {
    const element = preNavRef.current;

    if (!element) return;

    const observer = new ResizeObserver(() => {
      const height = element.offsetHeight;
      setPreNavHeight(height);
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, [showBanner]);

  useEffect(() => {
    const element = mainNavRef.current;

    if (!element) return;

    const observer = new ResizeObserver(() => {
      const height = element.offsetHeight;
      setMainNavHeight(height);
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, [showBanner]);

  const localStorageKey = `SHOW_PRENAV_BANNER`;
  useEffect(() => {
    const data = window.localStorage.getItem(localStorageKey);

    if (data === null) setShowBanner(true);
  }, []);

  useEffect(() => {
    sideNav ? lockScroll() : unlockScroll();
  }, [sideNav]);

  const md = useMediaQuery({
    query: "(max-width: 1055px)",
  });
  const xl = useMediaQuery({
    query: "(max-width: 1439px)",
  });

  const handleBannerCLose = () => {
    window.localStorage.setItem(localStorageKey, JSON.stringify(showBanner));
    setShowBanner(false);
  };

  return (
    <div className="w-full">
      <Headroom
        onPin={() => {
          setNavIsFixed(true);
          setNavIsUnpinned(false);
        }}
        onUnpin={() => {
          setNavIsFixed(true);
          setNavIsUnpinned(true);
        }}
        onUnfix={() => {
          setNavIsFixed(false);
        }}
        disableInlineStyles={true}
        pinStart={72}
      >
        <div />
      </Headroom>

      <div
        style={{
          height: preNavHeight + mainNavHeight,
        }}
      />

      <div
        className={classNames("fixed top-0 left-0 w-full z-50 shadow bg-White", "headroom-transition")}
        style={{
          transform:
            !navIsUnpinned && navIsFixed
              ? "translateY(0)"
              : !navIsUnpinned && !navIsFixed
              ? "translateY(0)"
              : `translateY(-${preNavHeight}px)`,
        }}
      >
        <div ref={preNavRef}>
          {bannerBlok && showBanner && !hideTopBanner && (
            <Banner {...bannerBlok} handleClose={() => handleBannerCLose()} />
          )}
          {!bannerBlok && isBrowser() && window?.location?.pathname.includes("dev-only/accessibility-test") && (
            <Banner
              heading="Accessibility Test Banner"
              subheading="Testing Environment"
              icon={{
                id: 0,
                alt: "",
                name: "",
                focus: "",
                title: "",
                filename: "",
                copyright: "",
                fieldtype: "",
                _uid: "dummy-icon",
              }}
              cta={[
                {
                  label: "Learn More",
                  link: { cached_url: "/accessibility-statement" },
                  type: "primary" as any,
                  size: "default" as any,
                },
              ]}
              handleClose={() => handleBannerCLose()}
            />
          )}
          <div className="hidden lg:block !bg-sherpa-red-main bg-Primary-900">
            <div className="container mx-auto">
              {pre_navigation_items && (
                <PreNav
                  links={pre_navigation_items}
                  login_link={login_link}
                  social_links={social_links}
                  is_find_us_search_enabled={is_find_us_search_enabled}
                  find_us_link={find_us_link}
                  find_us_placeholder={find_us_placeholder}
                  find_us_button_label={find_us_button_label}
                  pre_selected_filter_options={[]}
                ></PreNav>
              )}
            </div>
          </div>
        </div>
        <div className="container mx-auto" ref={mainNavRef}>
          {main_navigation_items && (
            <MainNav
              links={main_navigation_items}
              logo={logo}
              cta={primary_cta}
              is_search_enabled={is_search_enabled}
              search_link={search_link}
              search_bar_placeholder={search_bar_placeholder}
              search_bar_button_label={search_bar_button_label}
              toggleSideNav={toggleSideNav}
            ></MainNav>
          )}
        </div>
      </div>

      <div className="bg-White">
        <div className="container mx-auto">
          {main_navigation_items && pre_navigation_items && (
            <SideNav
              toggleSideNav={toggleSideNav}
              isActive={sideNav}
              pre_navigation_links={pre_navigation_items}
              login_link={login_link}
              main_navigation_links={main_navigation_items}
              social_links={social_links}
              is_find_us_search_enabled={is_find_us_search_enabled}
              find_us_link={find_us_link}
              find_us_placeholder={find_us_placeholder}
              find_us_button_label={find_us_button_label}
              pre_selected_filter_options={[]}
            ></SideNav>
          )}
        </div>
      </div>
    </div>
  );
};

export default Header;
